import { PlayerProperty, Save, Restore } from '../PlayerProperty';
import IPlayerData from '../../IPlayerData';
import { IPlayerState } from '../../../State/Player/IPlayerState';
import {
    dateToString,
    restoreBoolean,
    restoreDate,
    restoreNumber,
    restoreString,
    restoreStringifiedArray,
} from '../../ParseHelper';
import { report } from '../../../report';
import { IClaimedDailyLoginReward } from '../../../Constants/DailyLogin';
import { ProductId } from 'Utility/Product';

type PlayerStateKey = 'progress';
type PlayerDataKeys =
    | 'currencyShareCount'
    | 'wheelSpinCount'
    | 'loginStreak'
    | 'optInReward'
    | 'levelsSinceAd'
    | 'shopGiftsCollected'
    | 'adsWatched'
    | 'v1_45_0_totalAdsWatched'
    | 'difficultyReduction'
    | 'v1_44_0_starChestInitialStars'
    | 'v1_62_0_dailyLoginRewardsClaimed'
    | 'v2_7_1_payerReactivationPurchases';

export default class Progress
    implements PlayerProperty<PlayerStateKey, PlayerDataKeys> {
    public isChanged(
        playerStateData: IPlayerState,
        playerEndpointData: IPlayerData
    ): boolean {
        const saveData = this.save(playerStateData);
        return (
            playerEndpointData.currencyShareCount !==
                playerStateData.progress.currencyShareCount ||
            playerEndpointData.wheelSpinCount !==
                playerStateData.progress.wheelSpinCount ||
            playerEndpointData.loginStreak !==
                playerStateData.progress.loginStreak ||
            playerEndpointData.v1_62_0_dailyLoginRewardsClaimed !==
            saveData[
                    'v1_62_0_dailyLoginRewardsClaimed'
                ] ||
            playerEndpointData.optInReward !==
                playerStateData.progress.optInReward ||
            playerEndpointData.levelsSinceAd !==
                playerStateData.progress.levelsSinceAd ||
            playerEndpointData.shopGiftsCollected !==
                playerStateData.progress.shopGiftsCollected ||
            playerEndpointData.adsWatched !==
                playerStateData.progress.adsWatched ||
            playerEndpointData.v1_45_0_totalAdsWatched !==
                playerStateData.progress.totalAdsWatched ||
            playerEndpointData.difficultyReduction !==
                playerStateData.progress.difficultyReduction ||
            playerEndpointData.v1_44_0_starChestInitialStars !==
                playerStateData.progress.starChestInitialStars ||
            playerEndpointData.v2_7_1_payerReactivationPurchases !==
            saveData[
                'v2_7_1_payerReactivationPurchases'
            ]
        );
    }

    public restore(playerEndpointData: IPlayerData): Restore<PlayerStateKey> {
        const dailyLoginRewardsClaimedValues = restoreStringifiedArray<
            string | null
        >(playerEndpointData['v1_62_0_dailyLoginRewardsClaimed']);
        const dailyLoginRewardsClaimed: IClaimedDailyLoginReward[] = [];
        if (dailyLoginRewardsClaimedValues) {
            for (let i = 0; i < dailyLoginRewardsClaimedValues.length; i += 2) {
                try {
                    const id = restoreString(dailyLoginRewardsClaimedValues[i]);
                    const date = restoreDate(
                        dailyLoginRewardsClaimedValues[i + 1]
                    );
                    if (date === null) {
                        console.warn(
                            `Invalid timestamp: ${
                                dailyLoginRewardsClaimedValues[i + 1]
                            }`
                        );
                        throw new Error(
                            'Cannot parse timestamp of daily login reward'
                        );
                    }

                    dailyLoginRewardsClaimed.push({
                        id,
                        date,
                    });
                } catch (error) {
                    console.warn(
                        'Failed to parse claimed daily login rewards',
                        dailyLoginRewardsClaimedValues
                    );
                    report('Failed to parse claimed daily login rewards', {
                        error,
                        once: true,
                        sampleRate: 0.1,
                    });
                }
            }
        }

        return {
            currencyShareCount: restoreNumber(
                playerEndpointData.currencyShareCount,
                0
            ),
            wheelSpinCount: restoreNumber(playerEndpointData.wheelSpinCount, 0),
            loginStreak: restoreNumber(playerEndpointData.loginStreak, 0),
            optInReward: restoreNumber(playerEndpointData.optInReward, 0),
            levelsSinceAd: restoreNumber(playerEndpointData.levelsSinceAd, 0),
            shopGiftsCollected: restoreNumber(
                playerEndpointData.shopGiftsCollected,
                0
            ),
            adsWatched: restoreNumber(playerEndpointData.adsWatched, 0),
            totalAdsWatched: restoreNumber(
                playerEndpointData.v1_45_0_totalAdsWatched,
                0
            ),
            difficultyReduction: restoreNumber(
                playerEndpointData.difficultyReduction,
                0
            ),
            starChestInitialStars: restoreNumber(
                playerEndpointData.v1_44_0_starChestInitialStars,
                0
            ),
            dailyLoginRewardsClaimed,
            payerReactivationPurchases: restoreReactivationPurchases()
        };

        function restoreReactivationPurchases(): ProductId[] {
            const payerReactivationPurchases = restoreStringifiedArray<ProductId>(playerEndpointData.v2_7_1_payerReactivationPurchases, []);

            // If purchase was tracked using previous property, add offer to new list
            if (restoreBoolean(playerEndpointData.v1_75_0_payerReactivationOfferPurchased, false) && !payerReactivationPurchases.includes(ProductId.PAYER_REACTIVATION_OFFER)) {
                payerReactivationPurchases.push(ProductId.PAYER_REACTIVATION_OFFER);
            }

            return payerReactivationPurchases;
        }
    }

    public save(playerStateData: IPlayerState): Save<PlayerDataKeys> {
        const claimedDailyLoginRewardData: (string | Date)[] = [];
        for (const reward of playerStateData.progress
            .dailyLoginRewardsClaimed) {
            claimedDailyLoginRewardData.push(reward.id);
            claimedDailyLoginRewardData.push(dateToString(reward.date));
        }

        return {
            currencyShareCount: playerStateData.progress.currencyShareCount,
            wheelSpinCount: playerStateData.progress.wheelSpinCount,
            loginStreak: playerStateData.progress.loginStreak,
            optInReward: playerStateData.progress.optInReward,
            levelsSinceAd: playerStateData.progress.levelsSinceAd,
            shopGiftsCollected: playerStateData.progress.shopGiftsCollected,
            adsWatched: playerStateData.progress.adsWatched,
            v1_45_0_totalAdsWatched: playerStateData.progress.totalAdsWatched,
            difficultyReduction: playerStateData.progress.difficultyReduction,
            v1_44_0_starChestInitialStars:
                playerStateData.progress.starChestInitialStars,
            v1_62_0_dailyLoginRewardsClaimed: JSON.stringify(
                claimedDailyLoginRewardData
            ),
            v2_7_1_payerReactivationPurchases: JSON.stringify(
                playerStateData.progress.payerReactivationPurchases ?? []
            ),
        };
    }
}
